<template>
  <div v-if="puedeAdministrar" class="app-bar-action prueba-wa-app-bar">
    <v-btn
      icon
      small
      class="app-bar-btn"
      title="Prueba de rendimiento WhatsApp / Twilio"
      @click="dialogOpen = true"
    >
      <v-icon color="green darken-2">mdi-whatsapp</v-icon>
    </v-btn>

    <v-dialog
      v-model="dialogOpen"
      max-width="1100"
      scrollable
      content-class="prueba-wa-dialog"
    >
      <v-card flat outlined class="rounded-lg">
        <v-card-title class="py-3">
          <v-icon left color="green darken-2">mdi-whatsapp</v-icon>
          Prueba de rendimiento WhatsApp / Twilio
          <v-spacer></v-spacer>
          <v-btn icon small @click="dialogOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pt-4">
          <v-alert type="info" dense outlined class="mb-4">
            Al ejecutar la prueba, se emite un socket global. Los usuarios con puesto
            <strong>11</strong> (Twilio Unificado), <strong>18</strong> o <strong>19</strong> conectados
            ejecutarán su endpoint correspondiente y registrarán el tiempo de respuesta.
          </v-alert>

          <div class="d-flex flex-wrap align-center mb-4">
            <v-btn
              color="green darken-2"
              depressed
              class="rounded-lg mr-3 mb-2"
              :loading="ejecutando"
              @click="ejecutarPrueba"
            >
              <v-icon left>mdi-lightning-bolt</v-icon>
              Ejecutar prueba (socket)
            </v-btn>
            <v-chip v-if="ultimoBatchId" small outlined color="primary" class="mb-2">
              Lote: {{ ultimoBatchId.slice(0, 8) }}…
            </v-chip>
            <v-spacer></v-spacer>
            <v-btn
              small
              outlined
              color="teal darken-1"
              class="mb-2 mr-2"
              @click="dialogEncuestaOpen = true"
            >
              <v-icon left small>mdi-poll</v-icon>
              Ver encuesta
            </v-btn>
            <v-btn small outlined color="primary" class="mb-2" :loading="cargar" @click="cargarDatos">
              <v-icon left small>mdi-refresh</v-icon>
              Actualizar
            </v-btn>
          </div>

          <!-- KPIs -->
          <v-row dense class="mb-4">
            <v-col cols="6" sm="3">
              <v-card flat outlined class="rounded-lg pa-3 text-center">
                <div class="text-h6">{{ kpis.total_registros || 0 }}</div>
                <div class="caption grey--text">Registros</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card flat outlined class="rounded-lg pa-3 text-center">
                <div class="text-h6">{{ kpis.usuarios_unicos || 0 }}</div>
                <div class="caption grey--text">Usuarios</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card flat outlined class="rounded-lg pa-3 text-center">
                <div class="text-h6 success--text">{{ formatMs(kpis.promedio_tiempo_ms) }}</div>
                <div class="caption grey--text">Tiempo prom.</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card flat outlined class="rounded-lg pa-3 text-center">
                <div class="text-h6">{{ kpis.total_exito || 0 }}/{{ kpis.total_registros || 0 }}</div>
                <div class="caption grey--text">Éxitos</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Filtros -->
          <v-row dense class="mb-2">
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filtros.fechaInicio"
                label="Fecha inicio"
                type="date"
                filled
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filtros.fechaFin"
                label="Fecha fin"
                type="date"
                filled
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filtros.idPuesto"
                :items="puestosOpciones"
                item-text="text"
                item-value="value"
                label="Puesto"
                filled
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-select
                v-model="filtros.ruta"
                :items="rutasOpciones"
                item-text="text"
                item-value="value"
                label="Ruta"
                filled
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
          </v-row>

          <v-data-table
            :headers="headers"
            :items="registros"
            :loading="cargar"
            dense
            elevation="0"
            item-key="id"
            class="mt-3"
            :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
          >
            <template v-slot:item.created_at="{ item }">
              {{ formatFecha(item.created_at) }}
            </template>
            <template v-slot:item.usuario_nombre="{ item }">
              {{ item.usuario_nombre_erp || item.usuario_nombre || '—' }}
              <span class="caption grey--text">({{ item.id_usuario_erp }})</span>
            </template>
            <template v-slot:item.es_fast="{ item }">
              <v-chip x-small :color="item.es_fast ? 'orange' : 'blue'" text-color="white">
                {{ item.es_fast ? 'FAST' : 'INBI' }}
              </v-chip>
            </template>
            <template v-slot:item.tiempo_ms="{ item }">
              <span :class="tiempoClass(item.tiempo_ms)">{{ formatMs(item.tiempo_ms) }}</span>
            </template>
            <template v-slot:item.exito="{ item }">
              <v-icon x-small :color="item.exito ? 'success' : 'error'">
                {{ item.exito ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
            </template>
            <template v-slot:item.red="{ item }">
              <div class="caption">
                <v-icon x-small :color="item.online ? 'success' : 'error'">
                  {{ item.online ? 'mdi-wifi' : 'mdi-wifi-off' }}
                </v-icon>
                <span v-if="item.speed_mbps">{{ item.speed_mbps }} Mbps</span>
                <span v-if="item.ping_ms"> · {{ item.ping_ms }} ms</span>
                <span v-if="item.network_type"> · {{ item.network_type }}</span>
              </div>
            </template>
            <template v-slot:item.ruta_ejecutada="{ item }">
              <code class="caption">{{ item.ruta_ejecutada }}</code>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogEncuestaOpen"
      max-width="1100"
      scrollable
      content-class="prueba-wa-dialog"
    >
      <v-card flat outlined class="rounded-lg">
        <v-card-title class="py-3">
          <v-icon left color="teal darken-1">mdi-poll</v-icon>
          Encuesta Twilio — tiempo de carga
          <v-spacer></v-spacer>
          <v-btn icon small @click="dialogEncuestaOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pt-4">
          <v-alert type="info" dense outlined class="mb-4">
            Respuestas de usuarios con puesto <strong>11</strong>, <strong>18</strong> o <strong>19</strong>
            a la pregunta: <em>¿El tiempo es óptimo al cargar tus contactos y la conversación de Twilio?</em>
          </v-alert>

          <v-row dense class="mb-4">
            <v-col cols="6" sm="4">
              <v-card flat outlined class="rounded-lg pa-3 text-center">
                <div class="text-h6">{{ kpisEncuesta.total_respuestas || 0 }}</div>
                <div class="caption grey--text">Respuestas</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4">
              <v-card flat outlined class="rounded-lg pa-3 text-center">
                <div class="text-h6 success--text">{{ kpisEncuesta.total_si || 0 }}</div>
                <div class="caption grey--text">Sí (óptimo)</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4">
              <v-card flat outlined class="rounded-lg pa-3 text-center">
                <div class="text-h6 error--text">{{ kpisEncuesta.total_no || 0 }}</div>
                <div class="caption grey--text">No</div>
              </v-card>
            </v-col>
          </v-row>

          <v-card flat outlined class="rounded-lg mb-4">
            <v-card-title class="py-2 text-subtitle-2">
              <v-icon left small color="teal darken-1">mdi-chart-box-outline</v-icon>
              Reporte por punto de carga
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pt-3 pb-1">
              <v-data-table
                :headers="headersReporteEncuesta"
                :items="segmentosEncuesta"
                :loading="cargarEncuesta"
                dense
                elevation="0"
                item-key="key"
                hide-default-footer
                class="reporte-encuesta-table"
              >
                <template v-slot:item.label="{ item }">
                  <div class="font-weight-medium">{{ item.label }}</div>
                  <div class="caption grey--text">Puesto {{ item.puesto }} · {{ item.es_fast ? 'FAST' : 'INBI' }}</div>
                </template>
                <template v-slot:item.respondieron="{ item }">
                  <span class="font-weight-medium">{{ item.respondieron }}</span>
                  <span class="caption grey--text"> / {{ item.activos }} activos</span>
                </template>
                <template v-slot:item.total_si="{ item }">
                  <span class="success--text font-weight-medium">{{ item.total_si }}</span>
                </template>
                <template v-slot:item.total_no="{ item }">
                  <span class="error--text font-weight-medium">{{ item.total_no }}</span>
                </template>
                <template v-slot:item.porcentaje_aceptacion="{ item }">
                  <v-chip
                    x-small
                    :color="porcentajeAceptacionColor(item.porcentaje_aceptacion)"
                    text-color="white"
                  >
                    {{ item.porcentaje_aceptacion }} %
                  </v-chip>
                </template>
                <template v-slot:item.posible_problema="{ item }">
                  <v-chip
                    v-if="item.posible_problema && item.posible_problema !== 'N/A'"
                    x-small
                    :color="semaforoProblemaColor(item.posible_problema_semaforo)"
                    text-color="white"
                    class="posible-problema-chip"
                  >
                    {{ item.posible_problema }}
                  </v-chip>
                  <span v-else class="caption grey--text">{{ item.posible_problema || '—' }}</span>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <v-row dense class="mb-2">
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filtrosEncuesta.fechaInicio"
                label="Fecha inicio"
                type="date"
                filled
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filtrosEncuesta.fechaFin"
                label="Fecha fin"
                type="date"
                filled
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filtrosEncuesta.idPuesto"
                :items="puestosOpciones"
                item-text="text"
                item-value="value"
                label="Puesto"
                filled
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filtrosEncuesta.respuesta"
                :items="respuestaOpciones"
                item-text="text"
                item-value="value"
                label="Respuesta"
                filled
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
          </v-row>

          <div class="d-flex justify-end mb-2">
            <v-btn small outlined color="primary" :loading="cargarEncuesta" @click="cargarEncuestaDatos">
              <v-icon left small>mdi-refresh</v-icon>
              Actualizar
            </v-btn>
          </div>

          <v-data-table
            :headers="headersEncuesta"
            :items="registrosEncuesta"
            :loading="cargarEncuesta"
            dense
            elevation="0"
            item-key="id"
            :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
          >
            <template v-slot:item.created_at="{ item }">
              {{ formatFecha(item.created_at) }}
            </template>
            <template v-slot:item.usuario_nombre="{ item }">
              {{ item.usuario_nombre || '—' }}
              <span class="caption grey--text">({{ item.id_usuario_erp }})</span>
            </template>
            <template v-slot:item.puesto_nombre="{ item }">
              {{ item.puesto_nombre || ('Puesto ' + (item.id_puesto || '—')) }}
            </template>
            <template v-slot:item.plantel="{ item }">
              {{ item.plantel || '—' }}
            </template>
            <template v-slot:item.respuesta_optima="{ item }">
              <v-chip
                x-small
                :color="item.respuesta_optima ? 'success' : 'error'"
                text-color="white"
              >
                {{ item.respuesta_optima ? 'Sí' : 'No' }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import validarErrores from '@/mixins/validarErrores'
import {
  RUTAS_PRUEBA_WHATSAPP,
  PUESTOS_OPCIONES,
} from '@/utils/pruebaWhatsappRendimiento'

var ADMIN_IDERP = [1, 334, 568]

export default {
  name: 'PruebaWhatsappRendimientoAppBar',

  mixins: [validarErrores],

  data () {
    return {
      dialogOpen: false,
      dialogEncuestaOpen: false,
      ejecutando: false,
      cargar: false,
      cargarEncuesta: false,
      ultimoBatchId: null,
      registros: [],
      registrosEncuesta: [],
      kpis: {},
      kpisEncuesta: {},
      segmentosEncuesta: [],
      filtros: {
        fechaInicio: '',
        fechaFin: '',
        idPuesto: null,
        ruta: null,
      },
      filtrosEncuesta: {
        fechaInicio: '',
        fechaFin: '',
        idPuesto: null,
        respuesta: null,
      },
      puestosOpciones: [{ value: null, text: 'Todos' }].concat(PUESTOS_OPCIONES),
      respuestaOpciones: [
        { value: null, text: 'Todas' },
        { value: 1, text: 'Sí' },
        { value: 0, text: 'No' },
      ],
      rutasOpciones: [{ value: null, text: 'Todas' }].concat(RUTAS_PRUEBA_WHATSAPP),
      headers: [
        { text: 'Fecha', value: 'created_at', width: '150px' },
        { text: 'Usuario', value: 'usuario_nombre' },
        { text: 'Puesto', value: 'id_puesto', width: '70px' },
        { text: 'Escuela', value: 'es_fast', width: '80px' },
        { text: 'Ruta', value: 'ruta_ejecutada' },
        { text: 'Tiempo', value: 'tiempo_ms', width: '90px' },
        { text: 'OK', value: 'exito', width: '50px' },
        { text: 'Red / Latencia', value: 'red', sortable: false },
      ],
      headersEncuesta: [
        { text: 'Fecha', value: 'created_at', width: '150px' },
        { text: 'Usuario', value: 'usuario_nombre' },
        { text: 'Punto', value: 'punto_encuesta', width: '150px' },
        { text: 'Puesto', value: 'puesto_nombre', width: '120px' },
        { text: 'Plantel', value: 'plantel', width: '140px' },
        { text: 'Respuesta', value: 'respuesta_optima', width: '100px' },
      ],
      headersReporteEncuesta: [
        { text: 'Punto / Vista', value: 'label', sortable: false },
        { text: 'Activas respondieron', value: 'respondieron', width: '150px' },
        { text: 'Sí', value: 'total_si', width: '70px', align: 'center' },
        { text: 'No', value: 'total_no', width: '70px', align: 'center' },
        { text: '% Aceptación', value: 'porcentaje_aceptacion', width: '120px', align: 'center' },
        { text: 'Posible problema', value: 'posible_problema', sortable: false },
      ],
    }
  },

  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP']),

    puedeAdministrar () {
      var u = this.getdatosUsuarioERPERP
      return u && ADMIN_IDERP.includes(Number(u.iderp))
    },
  },

  watch: {
    dialogOpen (val) {
      if (val) this.cargarDatos()
    },
    dialogEncuestaOpen (val) {
      if (val) this.cargarEncuestaDatos()
    },
    filtros: {
      deep: true,
      handler () {
        if (this.dialogOpen) this.cargarDatos()
      },
    },
    filtrosEncuesta: {
      deep: true,
      handler () {
        if (this.dialogEncuestaOpen) this.cargarEncuestaDatos()
      },
    },
  },

  methods: {
    buildQuery () {
      var q = {}
      if (this.filtros.fechaInicio) q.fecha_inicio = this.filtros.fechaInicio
      if (this.filtros.fechaFin) q.fecha_fin = this.filtros.fechaFin
      if (this.filtros.idPuesto) q.id_puesto = this.filtros.idPuesto
      if (this.filtros.ruta) q.ruta = this.filtros.ruta
      q.limit = 100
      return q
    },

    buildEncuestaQuery () {
      var q = {}
      if (this.filtrosEncuesta.fechaInicio) q.fecha_inicio = this.filtrosEncuesta.fechaInicio
      if (this.filtrosEncuesta.fechaFin) q.fecha_fin = this.filtrosEncuesta.fechaFin
      if (this.filtrosEncuesta.idPuesto) q.id_puesto = this.filtrosEncuesta.idPuesto
      if (this.filtrosEncuesta.respuesta != null && this.filtrosEncuesta.respuesta !== '') {
        q.respuesta_optima = this.filtrosEncuesta.respuesta
      }
      q.limit = 200
      return q
    },

    async cargarEncuestaDatos () {
      if (!this.puedeAdministrar) return
      this.cargarEncuesta = true
      try {
        var q = this.buildEncuestaQuery()
        var qs = Object.keys(q).map(function (k) { return k + '=' + encodeURIComponent(q[k]) }).join('&')
        var listRes = await this.$http.get('encuesta-twilio.list?' + qs)
        var kpiRes = await this.$http.get('encuesta-twilio.kpis?' + qs)
        var reporteRes = await this.$http.get('encuesta-twilio.reporte?' + qs)
        this.registrosEncuesta = (listRes.data && listRes.data.rows) || []
        this.kpisEncuesta = kpiRes.data || {}
        this.segmentosEncuesta = (reporteRes.data && reporteRes.data.segmentos) || []
      } catch (error) {
        this.validarError((error.response && error.response.data && error.response.data.message) || 'Error al cargar encuesta')
      } finally {
        this.cargarEncuesta = false
      }
    },

    async cargarDatos () {
      if (!this.puedeAdministrar) return
      this.cargar = true
      try {
        var q = this.buildQuery()
        var qs = Object.keys(q).map(function (k) { return k + '=' + encodeURIComponent(q[k]) }).join('&')
        var listRes = await this.$http.get('prueba-whatsapp-rendimiento.list?' + qs)
        var kpiRes = await this.$http.get('prueba-whatsapp-rendimiento.kpis?' + qs)
        this.registros = (listRes.data && listRes.data.rows) || []
        this.kpis = kpiRes.data || {}
      } catch (error) {
        this.validarError((error.response && error.response.data && error.response.data.message) || 'Error al cargar pruebas')
      } finally {
        this.cargar = false
      }
    },

    async ejecutarPrueba () {
      this.ejecutando = true
      try {
        var res = await this.$http.post('prueba-whatsapp-rendimiento.ejecutar', {})
        this.ultimoBatchId = res.data && res.data.batchId
        this.validarSuccess('Socket emitido. Esperando respuestas de usuarios conectados…')
        setTimeout(function () { this.cargarDatos() }.bind(this), 3000)
        setTimeout(function () { this.cargarDatos() }.bind(this), 8000)
      } catch (error) {
        this.validarError((error.response && error.response.data && error.response.data.message) || 'Error al ejecutar prueba')
      } finally {
        this.ejecutando = false
      }
    },

    formatFecha (val) {
      if (!val) return '—'
      try {
        return new Date(val).toLocaleString('es-MX')
      } catch (e) {
        return val
      }
    },

    formatMs (val) {
      if (val == null || val === '') return '—'
      return Number(val) + ' ms'
    },

    tiempoClass (ms) {
      if (ms == null) return ''
      if (ms <= 2000) return 'success--text font-weight-medium'
      if (ms <= 5000) return 'warning--text font-weight-medium'
      return 'error--text font-weight-medium'
    },

    porcentajeAceptacionColor (valor) {
      var n = Number(valor)
      if (!Number.isFinite(n) || n <= 0) return 'grey'
      if (n >= 80) return 'success'
      if (n >= 50) return 'warning'
      return 'error'
    },

    semaforoProblemaColor (semaforo) {
      if (semaforo === 'red') return 'error'
      if (semaforo === 'yellow') return 'warning'
      if (semaforo === 'green') return 'success'
      return 'grey'
    },
  },
}
</script>

<style>
.prueba-wa-dialog {
  margin: 12px;
}

.posible-problema-chip {
  max-width: 100%;
  height: auto !important;
  white-space: normal;
  text-align: left;
}
</style>
